import { Button, ErrorAlert, Skeleton, cx } from "@unizpay/ui";
import { X } from "lucide-react";
import { Fragment, type ReactNode, useEffect, useRef, useState } from "react";
import { Navigate, Outlet } from "react-router";
import { maybeSignedIn } from "../lib/api";
import { ModeLimit, useMode } from "../lib/mode";
import { ProfileProvider, useProfile } from "../lib/profile";
import { useMe, useWallet } from "../lib/queries";
import type { Me } from "../lib/types";
import { OnboardingPage } from "../pages/OnboardingPage";
import { SetPinPage } from "../pages/SetPinPage";
import { SidebarNav } from "./SidebarNav";
import { TopBar } from "./TopBar";

/** Mobile navigation: the sidebar in a native modal <dialog> pinned to the left edge. */
function NavDrawer({ open, onClose, children }: { open: boolean; onClose: () => void; children: ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      onClose={() => open && onClose()}
      aria-label="Navigation"
      className="m-0 h-dvh max-h-none w-72 max-w-[85vw] border-r border-line bg-surface p-0 text-fg lg:hidden"
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute top-3.5 right-3 rounded-md p-1 text-fg-muted hover:bg-subtle hover:text-fg"
      >
        <X aria-hidden="true" className="size-5" />
        <span className="sr-only">Close navigation</span>
      </button>
      {children}
    </dialog>
  );
}

function PageSkeleton() {
  return (
    <output aria-label="Loading" className="block space-y-4">
      <Skeleton className="h-7 w-48" />
      <Skeleton className="h-4 w-80 max-w-full" />
      <Skeleton className="h-64 w-full" />
    </output>
  );
}

/** Dashboard chrome around the active profile's pages. */
export function DashboardShell() {
  const { mode } = useMode();
  const { profile } = useProfile();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const test = mode === "test";

  return (
    <div className="min-h-dvh">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:rounded-md focus:bg-surface focus:px-3 focus:py-2"
      >
        Skip to content
      </a>
      {test && (
        <div
          role="note"
          className="sticky top-0 z-30 flex h-7 items-center justify-center bg-test px-4 text-xs font-semibold text-test-fg"
        >
          Test mode<span className="hidden font-normal sm:inline">&nbsp;· You are viewing test data</span>
        </div>
      )}
      <div className="flex">
        <aside
          className={cx(
            "sticky hidden w-60 shrink-0 border-r border-line bg-surface lg:block",
            test ? "top-7 h-[calc(100dvh-1.75rem)]" : "top-0 h-dvh",
          )}
        >
          <SidebarNav />
        </aside>
        <NavDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)}>
          <SidebarNav onNavigate={() => setDrawerOpen(false)} />
        </NavDrawer>
        <div className="min-w-0 flex-1">
          <TopBar onOpenMenu={() => setDrawerOpen(true)} className={test ? "top-7" : "top-0"} />
          <main id="main" className="mx-auto w-full max-w-[1600px] px-4 py-6 sm:px-6 sm:py-8 lg:px-8">
            {/* Remount pages per profile so no form or local state carries over to another business. */}
            <Fragment key={profile.id}>
              <Outlet />
            </Fragment>
          </main>
        </div>
      </div>
    </div>
  );
}

/**
 * Authenticated area: loads the signed-in user, then renders the matched layout (the dashboard
 * shell, or a full-screen page such as the profile wizard). Any 401 from /dashboard-api
 * redirects to /login (see main.tsx). A user without a PIN sets one first (it approves every
 * sensitive action); then a user without profiles gets onboarding.
 */
export function AppLayout() {
  // Certainly signed out: go to sign-in without asking the API (which would log 401s).
  if (!maybeSignedIn()) return <Navigate to="/login" replace />;
  return <SignedInLayout />;
}

function SignedInLayout() {
  const me = useMe();
  const wallet = useWallet();

  if (me.isPending || (wallet.isPending && !me.isError)) {
    return (
      <div className="mx-auto w-full max-w-[1600px] px-4 py-10 sm:px-6 lg:px-8">
        <PageSkeleton />
      </div>
    );
  }
  if (me.isError) {
    return (
      <div className="mx-auto max-w-xl px-4 py-10">
        <ErrorAlert error={me.error} title="Could not load your account" />
        <Button variant="secondary" className="mt-4" onClick={() => me.refetch()} loading={me.isFetching}>
          Try again
        </Button>
      </div>
    );
  }
  return <ModeLimit testMode={me.data.test_mode}>{signedIn(me.data, wallet.data)}</ModeLimit>;
}

function signedIn(me: Me, wallet: { pin_set: boolean } | undefined) {
  // If the wallet cannot be loaded right now, the pages that need the PIN ask for it themselves.
  if (wallet && !wallet.pin_set) return <SetPinPage user={me.user} />;
  if (me.profiles.length === 0) return <OnboardingPage user={me.user} />;
  return (
    <ProfileProvider me={me}>
      <Outlet />
    </ProfileProvider>
  );
}
